import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { api, unwrap } from "@/lib/api";
import { authFetchJson } from "@/lib/auth-fetch";
import { useAuthStore } from "@/lib/auth";
import { formatMoney } from "@/lib/currency";
import {
  deriveTierLabel,
  normalizeOptions,
  useServiceDetail,
  type CustomField,
} from "@/lib/service-detail";
import { useServiceInventory } from "@/lib/inventory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "./Drawer";
import { TierPicker, type TierPickerStyle } from "./TierPicker";
import {
  ClientSelector,
  type ClientSelectorValue,
} from "./ClientSelector";
import { useToast } from "./Toast";

/**
 * Pencil component `WjxKr` (`v2/SidePanel — New Booking`).
 *
 * Sections in order: CLIENT (name + email + phone) / SERVICE DETAILS (service
 * + date + time + duration) / CUSTOM FIELDS (one input per field defined on
 * the chosen service — appears after a service is picked) / ADDITIONAL (notes).
 *
 * On submit POSTs to `/api/v1/bookings` with a `customFieldValues` array.
 * Service list is loaded from `/api/v1/services`; customer is upserted on
 * the API side by (tenant, email).
 */

const schema = z.object({
  serviceId: z.string().uuid("Pick a service"),
  email: z.string().email("Valid email required"),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  notes: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

export function NewBookingPanel({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (bookingId: string) => void;
}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  // Per-field values keyed by fieldId. Reset when the service changes so we
  // don't leak values from one service's schema into another.
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Inventory selections — one tier (radio) and many add-ons (checkbox).
  const [tierItemId, setTierItemId] = useState<string | null>(null);
  const [addonItemIds, setAddonItemIds] = useState<string[]>([]);

  const services = useQuery({
    queryKey: ["services.all"],
    enabled: !!accessToken && open,
    queryFn: async () => unwrap(await api.GET("/api/v1/services", {})),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Client section is driven by a typeahead selector — see `ClientSelector`.
  // Three modes: empty (popover search), existing (locked card), new (inline
  // form). On every change we mirror the value into the RHF fields so the
  // existing onSubmit body shape (email-based upsert) stays unchanged.
  const [client, setClient] = useState<ClientSelectorValue>({ kind: "empty" });
  // Capture the Drawer body element so the ClientSelector popover can
  // portal *inside* the Dialog's RemoveScroll shard. Without this,
  // wheel/touch scroll on the popover list is blocked. Callback ref +
  // state because React doesn't re-render on `.current` mutation.
  const [drawerBodyEl, setDrawerBodyEl] = useState<HTMLDivElement | null>(
    null,
  );
  useEffect(() => {
    if (client.kind === "existing") {
      const c = client.customer;
      setValue("firstName", c.firstName ?? "");
      setValue("lastName", c.lastName ?? "");
      setValue("email", c.email ?? "");
      setValue("phone", c.phone ?? "");
    } else if (client.kind === "new") {
      setValue("firstName", client.firstName ?? "");
      setValue("lastName", client.lastName ?? "");
      setValue("email", client.email);
      setValue("phone", client.phone ?? "");
    } else {
      setValue("firstName", "");
      setValue("lastName", "");
      setValue("email", "");
      setValue("phone", "");
    }
  }, [client, setValue]);

  // When the operator picks a service, load its schema fields + inventory
  // links (tiers/add-ons). Detail query is cache-shared with /services/:id.
  const selectedServiceId = watch("serviceId");
  const detail = useServiceDetail(selectedServiceId);
  const inventoryLinks = useServiceInventory(selectedServiceId);

  // The OpenAPI-generated `type` is `string`; runtime is one of the
  // ServiceFieldType members. Narrow with a cast here so the rest of the
  // component sees the strict union.
  const customFields = useMemo<CustomField[]>(
    () => (detail.data?.customFields ?? []) as CustomField[],
    [detail.data?.customFields],
  );

  const tierLinks = useMemo(
    () => (inventoryLinks.data ?? []).filter((l) => l.role === "tier"),
    [inventoryLinks.data],
  );
  const addonLinks = useMemo(
    () => (inventoryLinks.data ?? []).filter((l) => l.role === "addon"),
    [inventoryLinks.data],
  );

  useEffect(() => {
    // Reset every per-service input whenever the chosen service changes —
    // avoids carrying values + selections from a previously-selected service
    // whose schema or inventory differs.
    setFieldValues({});
    setFieldErrors({});
    setTierItemId(null);
    setAddonItemIds([]);
  }, [selectedServiceId]);

  // Seed default values from each field's settings.defaultValue once the
  // schema lands. Only fills fields the operator hasn't typed into yet.
  useEffect(() => {
    setFieldValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const f of customFields) {
        const dv = f.settings?.defaultValue;
        if (dv != null && dv !== "" && next[f.id] === undefined) {
          next[f.id] = dv;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [customFields]);

  // Live total: tier price + sum(addon prices). Falls back to 0 when nothing
  // is picked yet so the operator sees the total update as they tick boxes.
  const tierItem = tierLinks.find((l) => l.item.id === tierItemId)?.item;
  const selectedAddons = addonLinks.filter((l) => addonItemIds.includes(l.item.id));
  const tierPrice = Number(tierItem?.unitPrice ?? 0);
  const addonsTotal = selectedAddons.reduce(
    (sum, a) => sum + Number(a.item.unitPrice ?? 0),
    0,
  );
  const total = tierPrice + addonsTotal;
  const currency = tierItem?.currency ?? selectedAddons[0]?.item.currency ?? "PHP";

  const create = useMutation({
    mutationFn: async (v: FormValues) => {
      const body = {
        serviceId: v.serviceId,
        email: v.email,
        firstName: v.firstName?.trim() || undefined,
        lastName: v.lastName?.trim() || undefined,
        phone: v.phone?.trim() || undefined,
        notes: v.notes?.trim() || undefined,
        customFieldValues: customFields
          .map((f) => ({ fieldId: f.id, value: fieldValues[f.id]?.trim() ?? "" }))
          .filter((kv) => kv.value !== ""),
        tierItemId: tierItemId ?? undefined,
        addonItemIds: addonItemIds.length > 0 ? addonItemIds : undefined,
      };
      // Endpoint isn't in the generated types yet — use authFetchJson so a
      // 401 here triggers the same session-expired toast + redirect.
      return authFetchJson<{ id: string }>("/api/v1/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    onSuccess: (booking) => {
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["dashboard.overview"] });
      reset();
      setFieldValues({});
      setFieldErrors({});
      setClient({ kind: "empty" });
      onCreated?.(booking.id);
      onClose();
      toast.success("Booking created", "The new booking is in your queue.");
    },
    onError: (e) => {
      const msg = (e as Error).message;
      setError(msg);
      // Backend reports `missing_required_field:<label>` — surface it inline
      // on the offending field if we can match by label.
      const match = msg.match(/^missing_required_field:(.+)$/);
      if (match) {
        const label = match[1];
        const target = customFields.find((f) => f.label === label);
        if (target) {
          setFieldErrors((prev) => ({ ...prev, [target.id]: "Required" }));
        }
      }
      toast.error("Couldn't create booking", humanError(msg));
    },
  });

  // Client-side required-field guard — runs before we POST.
  const onSubmit = (v: FormValues) => {
    setError(null);
    // 1. Tier picker must be filled when the service has any tier links.
    if (tierLinks.length > 0 && !tierItemId) {
      toast.warning("Pick a tier", "Choose one of the plan options to continue.");
      return;
    }
    // 2. Required custom fields.
    const nextErrors: Record<string, string> = {};
    for (const f of customFields) {
      if (f.required && !(fieldValues[f.id] ?? "").trim()) {
        nextErrors[f.id] = "Required";
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      toast.warning(
        "Missing required fields",
        "Fill in the highlighted custom fields to continue.",
      );
      return;
    }
    setFieldErrors({});
    create.mutate(v);
  };

  return (
    <Drawer open={open} onClose={onClose} width="md">
      <DrawerHeader
        title="New Booking"
        subtitle="Fill in the booking details"
        onClose={onClose}
      />

      <DrawerBody ref={setDrawerBodyEl} className="px-5 py-4">
        <form
          id="new-booking-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <Section title="Client">
            <ClientSelector
              value={client}
              onChange={setClient}
              emailError={errors.email?.message}
              popoverContainer={drawerBodyEl}
            />
          </Section>

          <Section title="Service details">
            <div className="grid gap-1.5">
              <Label htmlFor="serviceId">Service type</Label>
              <Controller
                control={control}
                name="serviceId"
                render={({ field }) => (
                  <Select
                    value={field.value || undefined}
                    onValueChange={field.onChange}
                    disabled={services.isLoading}
                  >
                    <SelectTrigger id="serviceId" className="h-10">
                      <SelectValue
                        placeholder={
                          services.isLoading
                            ? "Loading services…"
                            : "Select a service…"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(services.data ?? []).map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.serviceId && (
                <p className="text-xs text-destructive">{errors.serviceId.message}</p>
              )}
            </div>
          </Section>

          {/* Plan tier picker — appears when the service offers tiers. The
              selected tier's price drives the booking total. Section title
              adapts: operator override → shared category → "Plan" fallback.
              Picker renders as radio cards or <select> based on
              service.tierPickerStyle. */}
          {selectedServiceId && tierLinks.length > 0 && (
            <Section
              title={deriveTierLabel(
                detail.data?.base as { tierLabel?: string | null } | undefined,
                tierLinks,
              )}
            >
              <TierPicker
                style={
                  ((detail.data?.base as { tierPickerStyle?: string })
                    ?.tierPickerStyle as TierPickerStyle | undefined) ?? "auto"
                }
                options={tierLinks.map((l) => ({
                  id: l.item.id,
                  name: l.item.name,
                  category: l.item.category,
                  unitPrice: l.item.unitPrice,
                  currency: l.item.currency,
                }))}
                value={tierItemId}
                onChange={setTierItemId}
              />
            </Section>
          )}

          {/* Add-ons — physical items the customer can opt into. Each adds to
              the total and decrements stock on submit. */}
          {selectedServiceId && addonLinks.length > 0 && (
            <Section title="Add-ons">
              <div className="grid gap-2">
                {addonLinks.map((l) => {
                  const checked = addonItemIds.includes(l.item.id);
                  // Plan-kind add-ons have no stock — they're never OOS.
                  // Only physicals at or below zero get the gated treatment.
                  const oos = l.item.kind === "physical" && l.item.qtyOnHand <= 0;
                  return (
                    <label
                      key={l.item.id}
                      className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
                        oos
                          ? "opacity-60 cursor-not-allowed border-border"
                          : checked
                            ? "border-primary bg-primary-light cursor-pointer"
                            : "border-border hover:bg-muted/40 cursor-pointer"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Checkbox
                          disabled={oos}
                          checked={checked}
                          onCheckedChange={(v) => {
                            setAddonItemIds((prev) =>
                              v === true
                                ? [...prev, l.item.id]
                                : prev.filter((id) => id !== l.item.id),
                            );
                          }}
                          aria-label={`Add ${l.item.name}`}
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {l.item.name}
                            {oos && (
                              <span className="text-xs text-status-cancelled ml-2">
                                · Out of stock
                              </span>
                            )}
                          </p>
                          {l.item.category && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {l.item.category}
                              {l.item.kind === "physical" &&
                                ` · ${l.item.qtyOnHand} in stock`}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold tabular-nums shrink-0">
                        +{formatMoney(l.item.currency, l.item.unitPrice)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Schema-driven section — appears as soon as a service is picked
              and only renders if that service has any custom fields. */}
          {selectedServiceId && customFields.length > 0 && (
            <Section title="Custom fields">
              {customFields.map((f) => (
                <CustomFieldInput
                  key={f.id}
                  field={f}
                  value={fieldValues[f.id] ?? ""}
                  error={fieldErrors[f.id]}
                  onChange={(v) =>
                    setFieldValues((prev) => ({ ...prev, [f.id]: v }))
                  }
                />
              ))}
            </Section>
          )}

          <Section title="Additional">
            <div className="grid gap-1.5">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                rows={3}
                placeholder="Anything the team should know…"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                {...register("notes")}
              />
            </div>
          </Section>

          {error && (
            <p className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
              {humanError(error)}
            </p>
          )}
        </form>
      </DrawerBody>

      <DrawerFooter className="justify-between">
        {tierLinks.length > 0 || addonLinks.length > 0 ? (
          <div className="text-sm">
            <span className="text-muted-foreground">Total</span>{" "}
            <span className="font-semibold tabular-nums">
              {formatMoney(currency, total)}
            </span>
          </div>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-booking-form"
            size="sm"
            disabled={isSubmitting || create.isPending}
          >
            {create.isPending ? "Creating…" : "Create Booking"}
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
}

/**
 * Renders the right input control for a `CustomField`. Type vocabulary
 * mirrors `ServiceFieldType` on the backend.
 */
function CustomFieldInput({
  field,
  value,
  error,
  onChange,
}: {
  field: CustomField;
  value: string;
  error?: string;
  onChange: (v: string) => void;
}) {
  const id = `cf-${field.id}`;
  const required = field.required;
  const placeholder = field.settings?.placeholder ?? undefined;
  const helpText = field.settings?.helpText ?? undefined;

  const labelEl = (
    <Label htmlFor={id} className="flex items-center gap-1">
      {field.label}
      {required && <span className="text-destructive">*</span>}
    </Label>
  );

  let control: React.ReactNode;

  switch (field.type) {
    case "textarea":
      control = (
        <textarea
          id={id}
          rows={3}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      );
      break;
    case "number":
      control = (
        <Input
          id={id}
          type="number"
          required={required}
          placeholder={placeholder}
          min={field.settings?.validation?.min}
          max={field.settings?.validation?.max}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case "date":
      control = (
        <Input
          id={id}
          type="date"
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case "datetime":
      control = (
        <Input
          id={id}
          type="datetime-local"
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;
    case "select": {
      const options = normalizeOptions(field.options);
      control = (
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger id={id} className="h-10">
            <SelectValue placeholder={placeholder ?? "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
      break;
    }
    case "checkbox":
      control = (
        <Switch
          checked={value === "true"}
          onCheckedChange={(v) => onChange(v ? "true" : "false")}
          aria-label={field.label}
        />
      );
      break;
    case "file":
      control = (
        <Input
          id={id}
          disabled
          placeholder="File upload — coming soon"
          className="cursor-not-allowed"
        />
      );
      break;
    case "text":
    default:
      control = (
        <Input
          id={id}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }

  return (
    <div className="grid gap-1.5">
      {labelEl}
      {control}
      {helpText && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
        {title}
      </p>
      {children}
    </section>
  );
}

function humanError(code: string): string {
  if (code.startsWith("missing_required_field:")) {
    return `Missing required field: ${code.slice("missing_required_field:".length)}`;
  }
  if (code.startsWith("addon_out_of_stock:")) {
    return `Out of stock: ${code.slice("addon_out_of_stock:".length)}`;
  }
  switch (code) {
    case "service_not_found":
      return "That service was removed. Pick another.";
    case "customer_not_found":
      return "Customer not found. Try entering an email instead.";
    case "customer_required":
      return "Add a client email or pick an existing customer.";
    case "field_not_in_service":
      return "One of the custom fields no longer belongs to that service.";
    case "tier_required":
      return "Pick a plan tier to continue.";
    case "tier_not_offered":
      return "That tier isn't available on this service.";
    case "addon_not_offered":
      return "One of the add-ons isn't available on this service.";
    case "missing_tenant_context":
      return "Session expired. Sign in again.";
    default:
      return code || "Could not create booking. Try again.";
  }
}
